import { defineMessages } from 'react-intl-next';
import type { MessageDescriptor } from 'react-intl-next';
import React, { type ReactNode } from 'react';

import { cards } from './assets/cards';
import {
	HTV2DownloadMobileAppCTA,
	HTV2AddProfilePictureCTA,
	HTV2ChangeSiteThemeCTA,
} from './cards/HTV2ActionCardLinkCTA';
import { HTV2ExplorePremiumCTA } from './cards/HTV2ExplorePremiumCTA';
import { HTV2CreatePageCTA, HTV2CreateWhiteboardCTA } from './cards/HTV2CreateContentCTA';
import { HTV2CreateSpaceCTA } from './cards/HTV2CreateSpaceCTA';

const i18n = defineMessages({
	createPageTitle: {
		id: 'experiment-home-onboarding-takeover-v2.create-page.title',
		defaultMessage: 'Create a page',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	createPageDescription: {
		id: 'experiment-home-onboarding-takeover-v2.create-page.description',
		defaultMessage:
			'Plan projects, take notes, and track tasks. Try a template or start from scratch.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	createPageCTA: {
		id: 'experiment-home-onboarding-takeover-v2.create-page.cta',
		defaultMessage: 'Create page',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will create a page and land them in the editor',
	},
	createSpaceTitle: {
		id: 'experiment-home-onboarding-takeover-v2.create-space.title',
		defaultMessage: 'Set up a team space',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	createSpaceDescription: {
		id: 'experiment-home-onboarding-takeover-v2.create-space.description',
		defaultMessage: 'Create a single source of truth for your team’s projects and docs.',
	},
	createWhiteboardTitle: {
		id: 'experiment-home-onboarding-takeover-v2.create-whiteboard.title',
		defaultMessage: 'Brainstorm on a whiteboard',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	createWhiteboardDescription: {
		id: 'experiment-home-onboarding-takeover-v2.create-whiteboard.description',
		defaultMessage: 'Dream up new ideas and visualize your work.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	createWhiteboardCTA: {
		id: 'experiment-home-onboarding-takeover-v2.create-whiteboard.cta',
		defaultMessage: 'Create whiteboard',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button will create a whiteboard and land them in the whiteboard editor',
	},
	addProfilePictureTitle: {
		id: 'experiment-home-onboarding-takeover-v2.add-profile-picture.title',
		defaultMessage: 'Add your profile photo',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	addProfilePictureDescription: {
		id: 'experiment-home-onboarding-takeover-v2.add-profile-picture.description',
		defaultMessage: 'Welcome your teammates to Confluence with a familiar face.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},

	changeSiteThemeTitle: {
		id: 'experiment-home-onboarding-takeover-v2.change-site-theme.title',
		defaultMessage: 'Add your company’s branding',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	changeSiteThemeDescription: {
		id: 'experiment-home-onboarding-takeover-v2.change-site-theme.description',
		defaultMessage: 'Give Confluence a familiar look by choosing colors and adding your logo.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},

	explorePremiumTitle: {
		id: 'experiment-home-onboarding-takeover-v2.explore-premium.title',
		defaultMessage: 'Try Premium features',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	explorePremiumDescription: {
		id: 'experiment-home-onboarding-takeover-v2.explore-premium.description',
		defaultMessage:
			'You’re on a 30-day free trial of Confluence Premium. See what’s included and take it out for a spin.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
	explorePremiumCTA: {
		id: 'experiment-home-onboarding-takeover-v2.explore-premium.cta',
		defaultMessage: 'See Premium features',
		description:
			'This will be shown on a button embedded into the home screen as a part of onboarding. Clicking the button open a modal that teaches them about premium features',
	},
	downloadMobileAppTitle: {
		id: 'experiment-home-onboarding-takeover-v2.download-mobile-app.title',
		defaultMessage: 'Take Confluence on the go',
		description:
			'This will be shown as a title on a card embedded into the home screen as a part of onboarding.',
	},
	downloadMobileAppDescription: {
		id: 'experiment-home-onboarding-takeover-v2.download-mobile-app.description',
		defaultMessage: 'Collaborate with your team and sync work across all of your devices.',
		description:
			'This will be shown as a description on a card embedded into the home screen as a part of onboarding.',
	},
});

export type OnboardingAction = {
	id: string;
	image: { light: string; dark: string };
	title: MessageDescriptor;
	description: MessageDescriptor;
	cta: ReactNode;
};

const ONBOARDING_ACTIONS: OnboardingAction[] = [
	{
		id: 'createPage',
		title: i18n.createPageTitle,
		description: i18n.createPageDescription,
		image: cards.createPage,
		cta: <HTV2CreatePageCTA />,
	},
	{
		id: 'createSpace',
		title: i18n.createSpaceTitle,
		description: i18n.createSpaceDescription,
		image: cards.createSpace,
		cta: <HTV2CreateSpaceCTA />,
	},
	{
		id: 'createWhiteboard',
		title: i18n.createWhiteboardTitle,
		description: i18n.createWhiteboardDescription,
		image: cards.createWhiteboard,
		cta: <HTV2CreateWhiteboardCTA />,
	},
	{
		id: 'addProfilePicture',
		title: i18n.addProfilePictureTitle,
		description: i18n.addProfilePictureDescription,
		image: cards.addProfilePicture,
		cta: <HTV2AddProfilePictureCTA />,
	},
	{
		id: 'changeSiteTheme',
		title: i18n.changeSiteThemeTitle,
		description: i18n.changeSiteThemeDescription,
		image: cards.changeSiteTheme,
		cta: <HTV2ChangeSiteThemeCTA />,
	},
	{
		id: 'explorePremium',
		title: i18n.explorePremiumTitle,
		description: i18n.explorePremiumDescription,
		image: cards.explorePremium,
		cta: <HTV2ExplorePremiumCTA />,
	},
	{
		id: 'downloadMobileApp',
		title: i18n.downloadMobileAppTitle,
		description: i18n.downloadMobileAppDescription,
		image: cards.downloadMobileApp,
		cta: <HTV2DownloadMobileAppCTA />,
	},
];

export function useOnboardingActions(): OnboardingAction[] {
	// TODO: Perform queries to hide the premium features card if it's not relevant.
	return ONBOARDING_ACTIONS;
}
